import React, { useState, useEffect } from 'react';
import Tree from 'react-d3-tree';
import LL from '../../../assets/Images/LL.png'
import RR from '../../../assets/Images/RR.png'
import LR from '../../../assets/Images/LR.png'
import RL from '../../../assets/Images/RL.png'
import '../styles/AVL.css'
import Button from "../../shared/Button";

class AVLNode {
  constructor(value) {
    this.value = value;
    this.left = null;
    this.right = null;
    this.height = 1;
  }
}

export default function AVLTreeVisualizer() {
  const [arrayInput, setArrayInput] = useState('');
  const [steps, setSteps] = useState([]);
  const [finalTreeData, setFinalTreeData] = useState(null);
  const [comparisonLogs, setComparisonLogs] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [showConditions, setShowConditions] = useState(false);

  const parseArrayInput = (inputStr) => {
    const tokens = inputStr.trim().split(/\s+/);
    const array = tokens.map((elem) => parseInt(elem, 10));
    return array.every((num) => !isNaN(num)) ? array : null;
  };

  const getHeight = (node) => {
    return node ? node.height : 0;
  };

  const getBalance = (node) => {
    return node ? getHeight(node.left) - getHeight(node.right) : 0;
  };

  const rotateRight = (y) => {
    const x = y.left;
    const T2 = x.right;

    x.right = y;
    y.left = T2;

    y.height = Math.max(getHeight(y.left), getHeight(y.right)) + 1;
    x.height = Math.max(getHeight(x.left), getHeight(x.right)) + 1;

    return x;
  };

  const rotateLeft = (x) => {
    const y = x.right;
    const T2 = y.left;

    y.left = x;
    x.right = T2;

    x.height = Math.max(getHeight(x.left), getHeight(x.right)) + 1;
    y.height = Math.max(getHeight(y.left), getHeight(y.right)) + 1;

    return y;
  };

  const insertNode = (node, value, stepLogs) => {
    if (!node) {
      stepLogs.push(`Inserting ${value}`);
      return new AVLNode(value);
    }

    if (value < node.value) {
      stepLogs.push(`${value} < ${node.value}, going left`);
      node.left = insertNode(node.left, value, stepLogs);
    } else if (value > node.value) {
      stepLogs.push(`${value} > ${node.value}, going right`);
      node.right = insertNode(node.right, value, stepLogs);
    } else {
      stepLogs.push(`${value} already exists, skipping`);
      return node;
    }

    node.height = 1 + Math.max(getHeight(node.left), getHeight(node.right));

    const balance = getBalance(node);

    stepLogs.push(`Balance factor of node ${node.value} is ${balance}`);

    if (balance > 1 && value < node.left.value) {
      stepLogs.push(`Right Rotation on node ${node.value}`);
      return rotateRight(node);
    }

    if (balance < -1 && value > node.right.value) {
      stepLogs.push(`Left Rotation on node ${node.value}`);
      return rotateLeft(node);
    }

    if (balance > 1 && value > node.left.value) {
      stepLogs.push(`Left-Right Rotation on node ${node.value}`);
      node.left = rotateLeft(node.left);
      return rotateRight(node);
    }

    if (balance < -1 && value < node.right.value) {
      stepLogs.push(`Right-Left Rotation on node ${node.value}`);
      node.right = rotateRight(node.right);
      return rotateLeft(node);
    }

    return node;
  };

  const buildAVLTree = () => {
    const array = parseArrayInput(arrayInput);
    if (!array) {
      alert('Invalid input! Please enter numbers separated by spaces.');
      return;
    }

    let root = null;
    const stepData = [];
    let allLogs = [];

    array.forEach(value => {
      const stepLogs = [];
      root = insertNode(root, value, stepLogs);
      allLogs = [...allLogs, ...stepLogs];
      stepData.push({
        tree: formatTreeData(root),
        logs: [...allLogs]
      });
    });

    setSteps(stepData);
    setFinalTreeData(formatTreeData(root));
    setComparisonLogs(allLogs);
    setCurrentStep(0);
  };

  const formatTreeData = (node) => {
    if (!node) return null;
    return {
      name: node.value.toString(),
      children: [
        formatTreeData(node.left),
        formatTreeData(node.right),
      ].filter(child => child !== null),
    };
  };

  return (
    <div className="avl-tree-visualizer">
      <div className="Msidebar">
        <h2>AVL Tree Visualizer</h2>
        <input
          className="array-input"
          value={arrayInput}
          onChange={(e) => setArrayInput(e.target.value)}
          placeholder="Enter numbers separated by spaces..."
        />
        <div className="button-container">
          <Button onClick={buildAVLTree}>Build AVL Tree</Button>
          <Button variant="" onClick={() => setShowConditions(true)}>AVL Tree Conditions</Button>
        </div>

        {comparisonLogs.length > 0 && (
          <div className="comparison-logs">
            <h3>Comparison Logs:</h3>
            <ul>
              {comparisonLogs.map((log, index) => (
                <li 
                  key={index} 
                  className={index < steps[currentStep]?.logs.length ? 'highlighted' : ''}
                >
                  {log}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="Amain-content">
        {steps.length > 0 && (
          <div className="step-trees">
            <h3>Step-by-Step Tree Construction:</h3>
            <div className="AVLstep-controls">
              <Button variant="secondary" size="sm" onClick={() => setCurrentStep(Math.max(0, currentStep - 1))} disabled={currentStep === 0}>Previous</Button>
              <span>Step {currentStep + 1} of {steps.length}</span>
              <Button variant="secondary" size="sm" onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))} disabled={currentStep === steps.length - 1}>Next</Button>
            </div>
            <div className="tree-container">
              <Tree
                data={steps[currentStep].tree}
                orientation="vertical"
                pathFunc="step"
                translate={{ x: 200, y: 50 }}
                separation={{ siblings: 1, nonSiblings: 2 }}
                renderCustomNodeElement={(rd3tProps) => (
                  <g>
                    <circle
                      r={22}
                      fill="#1e3a5f"
                      stroke="#60a5fa"
                      strokeWidth={2}
                    />
                    <text
                      fill="rgba(255,255,255,0.9)"
                      stroke="none"
                      x={0}
                      y={5}
                      fontSize={12}
                      fontWeight="600"
                      textAnchor="middle"
                    >
                      {rd3tProps.nodeDatum.name}
                    </text>
                  </g>
                )}
              />
            </div>
          </div>
        )}
      </div>

      {showConditions && (
        <div className="conditions-popup show">
          <Button variant="ghost" size="sm" onClick={() => setShowConditions(false)}>&times;</Button>
          <h2>AVL Tree Rotations</h2>
          <div className="rotation">
            <h3>Left Rotation</h3>
            <p>Used when a node becomes unbalanced with a right-heavy subtree.</p>
            <img src={LL} alt="Left Rotation Diagram" />
          </div>
          <div className="rotation">
            <h3>Right Rotation</h3>
            <p>Used when a node becomes unbalanced with a left-heavy subtree.</p>
            <img src={RR} alt="Right Rotation Diagram" />
          </div>
          <div className="rotation">
            <h3>Left-Right Rotation</h3>
            <p>A combination of left rotation followed by right rotation.</p>
            <img src={LR} alt="Left-Right Rotation Diagram" />
          </div>
          <div className="rotation">
            <h3>Right-Left Rotation</h3>
            <p>A combination of right rotation followed by left rotation.</p>
            <img src={RL} alt="Right-Left Rotation Diagram" />
          </div>
        </div>
      )}
    </div>
  );
}